import { useEffect, useState } from 'react'

import { Button, Loader, Text } from '@/ui'
import { OnboardingStickyNav } from '@/features/onboarding/shared/OnboardingStickyNav'
import { useFixedFooterOffset } from '@/features/onboarding/shared/useFixedFooterOffset'
import { getUnitJwt, GET_UNIT_JWT } from '@/actions/authActions'
import {
  ensureBankingProfile,
  ENSURE_BANKING_PROFILE_KEY,
} from '@/features/banking/bankingSlice'
import { BankingApplicationStatus } from '@/features/banking/banking.types'
import { parseUnitApplicationFormCompletedStatus } from '@/features/banking/banking.helpers'
import { UnitSessionGuard } from '@/features/banking/UnitSessionGuard'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { getFetchError } from '@/reducers/fetch'
import { useBankingAnalytics } from '@/features/banking/analytics'
import { ApplicationStatusModal } from './ApplicationStatusModal'

export interface BankingApplicationStageProps {
  /** Called after the outcome modal is closed (user finished/dismissed) */
  onContinue: () => void
  /** Called when the user clicks "Back" */
  onBack: () => void
  /** Called when the user clicks "Skip for now" */
  onSkip: () => void
}

export const BankingApplicationStage = ({
  onContinue,
  onBack,
  onSkip,
}: BankingApplicationStageProps) => {
  const dispatch = useAppDispatch()
  const [jwt, setJwt] = useState<string | null>(null)
  const [outcomeStatus, setOutcomeStatus] =
    useState<BankingApplicationStatus | null>(null)

  const ensureError = useReselector(getFetchError, ENSURE_BANKING_PROFILE_KEY)
  const jwtError = useReselector(getFetchError, GET_UNIT_JWT)
  const leftOffset = useFixedFooterOffset()

  const { trackApplicationOpened } = useBankingAnalytics()

  // Diagnostic event (not in the funnel): fire once when the user lands on
  // the application form. offer_source is always 'unknown' — see spec note
  // about cross-iframe attribution.
  useEffect(() => {
    trackApplicationOpened()
  }, [trackApplicationOpened])

  useEffect(() => {
    ;(async () => {
      // Drive the modal off persisted status so resumed sessions still surface terminal outcomes.
      const profile = await dispatch(ensureBankingProfile())
      if (profile?.applicationStatus) {
        setOutcomeStatus(profile.applicationStatus)
      }

      const res = await dispatch(getUnitJwt())
      if (res?.jwt) {
        setJwt(res.jwt)
      }
    })()
  }, [dispatch])

  useEffect(() => {
    if (!jwt) return undefined
    const el = document.querySelector('unit-elements-application-form')
    if (!el) return undefined

    // Read status from the event payload to avoid racing the webhook-driven
    // BankingProfile update. Resume case is covered by ensureBankingProfile on mount.
    // Unit delivers `detail` as a Promise, so the parser awaits it internally.
    const handler = async (event: Event) => {
      const status = await parseUnitApplicationFormCompletedStatus(event)
      setOutcomeStatus(status)
    }

    el.addEventListener('unitApplicationFormCompleted', handler)
    return () => el.removeEventListener('unitApplicationFormCompleted', handler)
  }, [jwt])

  const handleModalClose = () => {
    setOutcomeStatus(null)
    onContinue()
  }

  if (ensureError || jwtError) {
    return (
      <div className="flex flex-col items-center gap-4 p-8">
        <Text className="font-bold">
          We couldn&apos;t load your banking application.
        </Text>
        <Button variant="ghost" onClick={onBack}>
          Go back
        </Button>
      </div>
    )
  }

  if (!jwt) {
    return <Loader />
  }

  return (
    <UnitSessionGuard>
      {/* Exclude the Unit form (SSN, DOB, address) from Datadog RUM session replay. */}
      <div data-dd-privacy="hidden" className="pb-24 max-sm:pb-6">
        <unit-elements-application-form jwt-token={jwt} />
      </div>
      <OnboardingStickyNav
        leftOffset={leftOffset}
        className="mx-auto max-w-[1200px]"
        onBack={onBack}
        onSkip={onSkip}
        skipLabel="Skip for now"
      />
      <ApplicationStatusModal
        status={outcomeStatus}
        onClose={handleModalClose}
      />
    </UnitSessionGuard>
  )
}
